import React from 'react';
import Box from '@mui/material/Box';
import type { Claim } from './claims';
import { useNumberPopover, type ClaimArtifact } from './NumberPopover';

/**
 * A rendered value that is an object (#183). With a claim attached it is
 * interactive: hover highlights it, click opens the number popover with
 * its grounding and the four actions. Without a claim it renders its
 * children plain — prose numbers stay prose.
 */
export default function ClaimValue({ claim, children, sx, artifact }: { claim: Claim | null | undefined; children: React.ReactNode; sx?: any;
  /** the durable artifact the number sits in (a saved review's section), if any — what a re-grounding can be remembered on (#196) */
  artifact?: ClaimArtifact | null }) {
  const { open } = useNumberPopover();
  if (!claim) return <>{children}</>;
  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      data-testid="claim-value"
      data-claim-id={claim.id}
      aria-label={`${claim.label || claim.grounding?.metric || 'value'}: open number actions`}
      onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); open(claim, e.currentTarget, null, artifact); }}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(claim, e.currentTarget, null, artifact); }
      }}
      sx={{
        cursor: 'pointer',
        borderRadius: '4px',
        px: '2px',
        mx: '-2px',
        transition: 'color 100ms, background-color 100ms, box-shadow 100ms',
        '&:hover, &:focus-visible': { color: 'primary.dark', bgcolor: 'var(--accent-tint)', boxShadow: 'inset 0 -1px 0 var(--accent)', outline: 'none' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
